import { Card, Table, useBreakpointValue } from '@chakra-ui/react';
import { CustomerPortalInvoice, InvoiceStatusEnum } from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import {
  MBadge,
  MBox,
  MButton,
  MCard,
  MFlex,
  MGrid,
  MText,
} from '../../../../components/Monetize';
import {
  TableCell,
  TableColumnHeader,
} from '../../../../components/SubscriptionView/SubscriptionViewTable';
import { isInvoicePastDue } from '../invoice.utils';

const getStatusColorPalette = (
  status: CustomerPortalInvoice['status'],
  pastDue: boolean,
) => {
  if (pastDue) {
    return 'red';
  }
  switch (status) {
    case InvoiceStatusEnum.PAID:
      return 'green';
    case InvoiceStatusEnum.UNPAID:
      return 'orange';
    default:
      return 'gray';
  }
};

const InvoiceStatusBadge = ({
  invoice,
  mt,
}: {
  invoice: CustomerPortalInvoice;
  mt?: number;
}) => {
  const pastDue = isInvoicePastDue(invoice);
  return (
    <MBadge
      colorPalette={getStatusColorPalette(invoice.status, pastDue)}
      variant="subtle"
      // v2's badge theme uppercased every badge; v3's doesn't.
      textTransform="uppercase"
      px={2}
      py={0.5}
      borderRadius="full"
      fontSize="xs"
      fontWeight="600"
      mt={mt}
    >
      {pastDue ? 'Past Due' : invoice.status}
    </MBadge>
  );
};

const DueDateCell = ({ invoice }: { invoice: CustomerPortalInvoice }) => {
  const pastDue = isInvoicePastDue(invoice);
  return (
    <MBox>
      <MText
        fontSize="sm"
        color={pastDue ? 'tRed.base' : 'fg.primary'}
        fontWeight={pastDue ? 600 : undefined}
        lineHeight="short"
      >
        {toDateShort(invoice.dueDate)}
      </MText>
    </MBox>
  );
};

interface InvoicesCardProps {
  invoices: CustomerPortalInvoice[];
  hasLoadError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  canPayInvoices: boolean;
  onLoadMore: () => void;
  onView: (invoice: CustomerPortalInvoice) => void;
  onPay: (invoice: CustomerPortalInvoice) => void;
}

export const InvoicesCard = ({
  invoices,
  hasLoadError,
  hasMore,
  isLoadingMore,
  canPayInvoices,
  onLoadMore,
  onView,
  onPay,
}: InvoicesCardProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const showEmptyState = invoices.length === 0;
  const showErrorState = hasLoadError;
  const showMobileState = !showEmptyState && !showErrorState && isMobile;
  const showDesktopState = !showEmptyState && !showErrorState && !isMobile;

  return (
    <MCard variant="border" borderRadius="md">
      <Card.Header pt={4} pb={2} px={4}>
        <MText fontWeight="bold" fontSize="lg" color="tPurple.dark">
          Invoices
        </MText>
      </Card.Header>
      <Card.Body pt={0} px={0}>
        {showEmptyState && (
          <MFlex py={8} justifyContent="center">
            <MText color="tGray.darkPurple">No invoices found</MText>
          </MFlex>
        )}
        {showErrorState && (
          <MFlex py={8} justifyContent="center">
            <MText color="tGray.darkPurple">Unable to load invoices</MText>
          </MFlex>
        )}
        {showMobileState && (
          <MBox>
            {invoices.map((invoice) => (
              <InvoiceMobileCard
                key={invoice.id}
                invoice={invoice}
                canPayInvoices={canPayInvoices}
                onView={onView}
                onPay={onPay}
              />
            ))}
          </MBox>
        )}
        {showDesktopState && (
          <MBox overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <TableColumnHeader>Invoice #</TableColumnHeader>
                  <TableColumnHeader>Bill Date</TableColumnHeader>
                  <TableColumnHeader>Due Date</TableColumnHeader>
                  <TableColumnHeader textAlign="end">Amount</TableColumnHeader>
                  <TableColumnHeader textAlign="end">
                    Amount Due
                  </TableColumnHeader>
                  <TableColumnHeader>Status</TableColumnHeader>
                  <TableColumnHeader textAlign="right">
                    <MBox display="none">Actions</MBox>
                  </TableColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    canPayInvoices={canPayInvoices}
                    onView={onView}
                    onPay={onPay}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          </MBox>
        )}
        {hasMore && (
          <MFlex justifyContent="center" py={4}>
            <MButton
              type="button"
              variant="tertiary"
              onClick={onLoadMore}
              isLoading={isLoadingMore}
              isDisabled={isLoadingMore}
            >
              Load More
            </MButton>
          </MFlex>
        )}
      </Card.Body>
    </MCard>
  );
};

const InvoiceRow = ({
  invoice,
  canPayInvoices,
  onView,
  onPay,
}: {
  invoice: CustomerPortalInvoice;
  canPayInvoices: boolean;
  onView: (invoice: CustomerPortalInvoice) => void;
  onPay: (invoice: CustomerPortalInvoice) => void;
}) => {
  const canPay = canPayInvoices && invoice.status === InvoiceStatusEnum.UNPAID;

  return (
    <Table.Row
      key={invoice.id}
      borderBottom="1px solid #e4e4e4"
      _hover={{
        backgroundColor: '#fafaff',
        borderBottomColor: '#4659da',
      }}
    >
      <TableCell fontWeight="500">{invoice.invoiceNumber}</TableCell>
      <TableCell fontSize="sm" color="tPurple.dark" borderBottom="none">
        {toDateShort(invoice.billDate)}
      </TableCell>
      <TableCell borderBottom="none">
        <DueDateCell invoice={invoice} />
      </TableCell>
      <TableCell
        fontSize="sm"
        fontWeight="600"
        color="tPurple.dark"
        textAlign="end"
        borderBottom="none"
      >
        {formatCurrency(invoice.amount, {
          currency: invoice.currency,
          maximumFractionDigits: 2,
        })}
      </TableCell>
      <TableCell
        fontSize="sm"
        fontWeight="600"
        color="tPurple.dark"
        textAlign="end"
        borderBottom="none"
      >
        {formatCurrency(invoice.amountDue, {
          currency: invoice.currency,
          maximumFractionDigits: 2,
        })}
      </TableCell>
      <TableCell borderBottom="none">
        <InvoiceStatusBadge invoice={invoice} />
      </TableCell>
      <TableCell borderBottom="none" textAlign="right">
        <MFlex gap={1} justifyContent="flex-end" alignItems="center">
          {canPay && (
            <MButton
              variant="tertiary"
              size="sm"
              onClick={() => onPay(invoice)}
            >
              Pay
            </MButton>
          )}
          <MButton variant="tertiary" size="sm" onClick={() => onView(invoice)}>
            View
          </MButton>
        </MFlex>
      </TableCell>
    </Table.Row>
  );
};

const InvoiceMobileCard = ({
  invoice,
  canPayInvoices,
  onView,
  onPay,
}: {
  invoice: CustomerPortalInvoice;
  canPayInvoices: boolean;
  onView: (invoice: CustomerPortalInvoice) => void;
  onPay: (invoice: CustomerPortalInvoice) => void;
}) => {
  const canPay = canPayInvoices && invoice.status === InvoiceStatusEnum.UNPAID;

  return (
    <MBox
      p={4}
      borderBottom="1px solid"
      borderColor="gray.200"
      _last={{ borderBottom: 'none' }}
    >
      <MFlex justifyContent="space-between" alignItems="flex-start" mb={3}>
        <MBox>
          <MText fontWeight="600" color="tPurple.dark">
            {invoice.invoiceNumber}
          </MText>
          <InvoiceStatusBadge invoice={invoice} mt={1} />
        </MBox>
        <MText fontWeight="700" color="tPurple.dark" fontSize="md">
          {formatCurrency(invoice.amount, {
            currency: invoice.currency,
            maximumFractionDigits: 2,
          })}
        </MText>
        <MText fontWeight="700" color="tPurple.dark" fontSize="md">
          {formatCurrency(invoice.amountDue, {
            currency: invoice.currency,
            maximumFractionDigits: 2,
          })}
        </MText>
      </MFlex>
      <MGrid templateColumns="1fr 1fr" gap={2} mb={3}>
        <MBox>
          <MText fontSize="xs" color="tGray.darkPurple">
            Bill Date
          </MText>
          <MText fontSize="sm" color="tPurple.dark">
            {toDateShort(invoice.billDate)}
          </MText>
        </MBox>
        <MBox>
          <MText fontSize="xs" color="tGray.darkPurple">
            Due Date
          </MText>
          <DueDateCell invoice={invoice} />
        </MBox>
      </MGrid>
      <MFlex gap={2} alignItems="center">
        {canPay && (
          <MButton variant="tertiary" size="sm" onClick={() => onPay(invoice)}>
            Pay
          </MButton>
        )}
        <MButton variant="tertiary" size="sm" onClick={() => onView(invoice)}>
          View
        </MButton>
      </MFlex>
    </MBox>
  );
};
